/** Student form helpers: DTO builders, hydration, validation. */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCompanyCountry } from '@/stores/themeStore';
import {
  formBool,
  hydratePhoneFromApi,
  hydrateUserContactFields,
  phoneToE164,
  primaryContactKindFromType,
  assignPrimaryContactFields,
} from '@/utils/user-contact-utils';
import { getSportClubs } from '@/services/sport-clubs.service';
import { uploadStudentSchoolReport } from '@/services/files.service';
import {
  STUDENT_ACCOUNT_STATUS_CONFIG,
  STUDENT_SITUATION_CONFIG,
} from '@/components/students/student-configs';
import {
  assignDefined,
  assignTruthy,
  buildArchiveDto as buildSharedArchiveDto,
  formatReactivationPreview as formatSharedReactivationPreview,
  isArchiveReason as isSharedArchiveReason,
  normalizeHighSchoolCurriculum,
  normalizeSchoolCurriculum,
  normalizeSchoolSituation,
  parseDepartmentFromLabel,
  setNestedPath,
} from '@/utils/common-utils';
import type { Program } from '@/types/program.types';
import type { StudentRecord, StudentSituation } from '@/types/remote/student.types';
import { applyCatalogCoursePath } from '@/utils/program-cascade-utils';
import type {
  ArchiveReason,
  ArchiveRemoteUserDto,
  ContactKind,
  ReactivationPreview,
} from '@/types/user.types';
import { getPrimaryRole } from '@/utils/primary-role-utils';
import { storedDocumentLink } from '@/utils/pending-document-link-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { schoolEstablishmentRefFromId } from '@/utils/other-establishment-utils';

export { normalizeSchoolCurriculum, normalizeSchoolSituation } from '@/utils/common-utils';

/** Email vs phone contact kind used by the remote student form. */
type ContactType = ContactKind;

/** Create vs edit student form. */
export type FormMode = 'create' | 'edit';
/** Linked parent email/phone used for minor contact fallback. */
type ParentContact = { email?: string; phone?: string };

/** Form patch + contact type when inheriting a parent contact. */
type ParentContactPrefill = {
  patch: Record<string, any>;
  contactType: ContactType;
  secondaryContact?: string;
};

/** Parent GET payload shape used to inherit contact for a minor. */
type ParentContactRecord = {
  user?: { email?: string | null; phone?: string | null };
  sections?: Record<string, { email?: string; phoneNumber?: string } | undefined>;
};

/** Visual state for the adult contact field on create. */
type AdultContactFieldState = 'default' | 'error' | 'success' | undefined;

/** Archive DTO from a reason + comment. */
export function buildArchiveDto(reason: ArchiveReason, comment: string): ArchiveRemoteUserDto {
  return buildSharedArchiveDto(reason, comment);
}

/** True when the value is a known archive reason. */
export function isArchiveReason(value: string): value is ArchiveReason {
  return isSharedArchiveReason(value);
}

/** Age in full years from a date-of-birth string. */
function calcAge(dob: string): number {
  const d = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  if (today < new Date(today.getFullYear(), d.getMonth(), d.getDate())) age--;
  return age;
}

/** True when DOB is set and age is 18+. */
export function isAdultFromDob(dob: string | undefined): boolean {
  if (!dob) return false;
  return calcAge(dob) >= 18;
}

/** Flatten onboarding sections; keep `documents.schoolReport` when present. */
function mergeOnboardingSections(record: StudentRecord): Record<string, any> {
  const sections = record.sections ?? {};
  let merged: Record<string, any> = {};
  for (const sec of Object.values(sections)) {
    if (sec && typeof sec === 'object') {
      merged = { ...merged, ...(sec as Record<string, any>) };
    }
  }
  const docsSec = (sections as any).documents as Record<string, any> | undefined;
  if (docsSec !== undefined && docsSec !== null && 'schoolReport' in docsSec) {
    merged.schoolReport = docsSec.schoolReport ?? {};
  }
  merged.isPartner = record.isPartner;
  return merged;
}

/** Overlay user identity + primary contact onto the merged form. */
function normalizeIdentityFields(
  merged: Record<string, any>,
  record: StudentRecord,
  defaultCountry: string,
): void {
  merged.firstName = merged.firstName ?? record.user?.firstName ?? '';
  merged.lastName = merged.lastName ?? record.user?.lastName ?? '';
  merged.status = merged.status ?? record.user?.status ?? '';
  if (!merged.parentUserId && record.linkedUserId) {
    merged.parentUserId = record.linkedUserId;
  }
  const hydrated = hydrateUserContactFields(
    record.user?.email,
    record.user?.phone,
    defaultCountry,
    record.user?.primaryContactKind,
  );
  merged.contactType = merged.contactType ?? hydrated.contactType;
  if (merged.contactType === 'phone') {
    const parsed = hydratePhoneFromApi(merged.contact || hydrated.contact, defaultCountry);
    merged.contact = parsed.value;
    merged.contactCountryCode = parsed.countryCode;
  } else {
    merged.contact = merged.contact ?? hydrated.contact;
  }
  merged.secondaryContact = merged.secondaryContact ?? hydrated.secondaryContact;
  merged.secondaryContactCountryCode =
    merged.secondaryContactCountryCode ?? hydrated.secondaryContactCountryCode;
  if (merged.phoneNumber && !merged.phone) {
    merged.phone = hydratePhoneFromApi(merged.phoneNumber, defaultCountry).value;
  }
}

/** Unwrap `level` object/string onto `levelId`. */
function normalizeLevelField(merged: Record<string, any>): void {
  if (merged.level && typeof merged.level === 'object') {
    merged.levelId = (merged.level as any).levelId ?? merged.level;
  } else if (merged.level) {
    merged.levelId = merged.level;
  }
}

/** School establishments + department code as ids the selects understand. */
function normalizeEstablishmentFields(merged: Record<string, any>): void {
  if (merged.schoolEstablishment) {
    const estArr = Array.isArray(merged.schoolEstablishment)
      ? merged.schoolEstablishment
      : [merged.schoolEstablishment];
    merged.schoolEstablishment = estArr
      .filter((e: any) =>
        typeof e === 'string' ? !!e : !!e.establishmentId || !!e.establishmentName,
      )
      .map((e: any) =>
        schoolEstablishmentRefFromId(
          typeof e === 'string' ? e : (e.establishmentId ?? ''),
          typeof e === 'string' ? undefined : e.establishmentName,
        ),
      );
    merged.schoolEstablishmentIds = merged.schoolEstablishment
      .map((e: { establishmentId: string }) => e.establishmentId)
      .filter((id: string) => !!id);
  }

  if (merged.establishmentDepartment) {
    merged.establishmentDepartmentCode =
      typeof merged.establishmentDepartment === 'string'
        ? merged.establishmentDepartment
        : ((merged.establishmentDepartment as any)?.departmentId ?? '');
  }
}

/** Split department string/object into `departmentId` / `departmentName`. */
function normalizeDepartmentFields(merged: Record<string, any>): void {
  if (merged.department && typeof merged.department === 'string') {
    merged.departmentId = merged.department;
    merged.departmentName = '';
  } else if (merged.department && typeof merged.department === 'object') {
    merged.departmentId = (merged.department as any).departmentId ?? '';
    merged.departmentName = (merged.department as any).departmentName ?? '';
  }

  if (!merged.establishmentDepartmentCode && merged.departmentId) {
    merged.establishmentDepartmentCode = merged.departmentId;
  }
}

/** Programs, partner radio, living-with-parents, school enums, availability slots. */
function normalizeMatchingAndSchoolFields(merged: Record<string, any>): void {
  if (merged.programs) {
    merged.programs = Array.isArray(merged.programs)
      ? merged.programs.map((p: any) => p.programId ?? p)
      : [];
  }

  merged.isPartner = formBool(merged.isPartner) ? 'true' : 'false';
  merged.availableOnVacations = formBool(merged.availableOnVacations);
  merged.availableOnWeekends = formBool(merged.availableOnWeekends);
  merged.availableOnHolidays = formBool(merged.availableOnHolidays);
  merged.hasParticipated = formBool(merged.hasParticipated);

  const linkedParentId = linkedParentUserId(merged.parentUserId);
  if (!linkedParentId) {
    // A stored Yes without a linked parent must not lock address fields.
    merged.livingWithParents = undefined;
  } else {
    merged.livingWithParents = formBool(merged.livingWithParents);
  }

  if (merged.schoolSituation) {
    merged.schoolSituation = normalizeSchoolSituation(merged.schoolSituation);
  }
  if (merged.schoolCurriculum && typeof merged.schoolCurriculum === 'string') {
    merged.schoolCurriculum = normalizeSchoolCurriculum(merged.schoolCurriculum);
  }
  if (merged.highSchoolType && typeof merged.highSchoolType === 'string') {
    merged.highSchoolType = normalizeHighSchoolCurriculum(merged.highSchoolType);
  }

  if (Array.isArray(merged.availability)) {
    merged.availability = (merged.availability as any[]).map((slot: any) => ({
      day: slot.dayOfTheWeek ?? slot.day,
      start: slot.startTime ?? slot.start,
      end: slot.endTime ?? slot.end,
    }));
  }
}

/**
 * Unified normalization: merges all sections from a {@link StudentRecord} into
 * a flat form object with canonical field mappings, arrays, and radio conversions.
 * Used for both initial record prop prefill and authoritative fullState arrival.
 */
function normalizeMergedStudentForm(
  record: StudentRecord,
  defaultCountry: string,
): Record<string, any> {
  const merged = mergeOnboardingSections(record);
  normalizeIdentityFields(merged, record, defaultCountry);
  normalizeLevelField(merged);
  normalizeEstablishmentFields(merged);
  normalizeDepartmentFields(merged);
  normalizeMatchingAndSchoolFields(merged);
  return merged;
}

/** Stage-2 hydrate: same as normalize + mobile partner/subject fallbacks. */
export function hydrateFullStateStudentForm(
  record: StudentRecord,
  defaultCountry: string,
  programs?: ReadonlyArray<Pick<Program, 'id' | 'coursePathType'>>,
): Record<string, any> {
  const merged = normalizeMergedStudentForm(record, defaultCountry);
  if (!merged.partnerName && merged.partner) {
    merged.partnerName = merged.partner;
  }
  if (merged.subject && /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(merged.subject)) {
    merged.subject = '';
  }
  if (!merged.country) merged.country = getCompanyCountry();
  if (merged.coursePath === 'classic') merged.coursePath = 'standard';
  applyCatalogCoursePath(merged, programs);
  return merged;
}

/** Empty create form, optionally pre-linked to a parent. */
export function buildCreateInitialData(initialParentUserId?: string): Record<string, any> {
  const create: Record<string, any> = {
    livingWithParents: false,
    availableOnVacations: false,
    availableOnWeekends: false,
    availableOnHolidays: false,
    isPartner: false,
    hasParticipated: false,
    country: getCompanyCountry(),
  };
  if (initialParentUserId) {
    create.parentUserId = initialParentUserId;
  }
  return create;
}

/** Parent userId → email/phone map, including the initial linked parent. */
export function buildParentContactMap(
  parentResponse: any,
  initialParentUserId?: string,
  initialParentEmail?: string,
  initialParentPhone?: string,
): Record<string, ParentContact> {
  const map: Record<string, ParentContact> = {};
  if (initialParentUserId) {
    map[initialParentUserId] = { email: initialParentEmail, phone: initialParentPhone };
  }
  for (const r of parentResponse?.data ?? []) {
    map[r.userId] = extractParentContactFromRecord(r) ?? {
      email: r.user?.email,
      phone: r.user?.phone,
    };
  }
  return map;
}

/** Parent select options: name, then email, then userId. */
export function buildParentOptions(parentResponse: any): Array<{ value: string; label: string }> {
  return (parentResponse?.data ?? []).map((r: any) => ({
    value: r.userId,
    label:
      `${r.user?.firstName ?? ''} ${r.user?.lastName ?? ''}`.trim() || r.user?.email || r.userId,
  }));
}

/** Unwrap a parent payload that may nest the record under `.record`. */
function parentContactRecordFromPayload(payload: unknown): ParentContactRecord | null {
  if (!payload || typeof payload !== 'object') return null;
  const obj = payload as { record?: ParentContactRecord } & ParentContactRecord;
  if (obj.record && (obj.record.user || obj.record.sections)) return obj.record;
  if (obj.user || obj.sections) return obj;
  return null;
}

/** Email and/or phone inherited from a linked parent record. */
function extractParentContactFromRecord(payload: unknown): ParentContact | null {
  const record = parentContactRecordFromPayload(payload);
  if (!record) return null;
  const basic = record.sections?.basic_information;
  const email = record.user?.email?.trim() || basic?.email?.trim() || undefined;
  const phone = record.user?.phone?.trim() || basic?.phoneNumber?.trim() || undefined;
  if (!email && !phone) return null;
  return { email, phone };
}

/** Parent email/phone from a parent full-state payload. */
export function extractParentContactFromFullState(parentFullState: unknown): ParentContact | null {
  return extractParentContactFromRecord(parentFullState);
}

/** Prefer parent email as primary; otherwise phone. */
export function resolveParentContactPrefill(
  parentContact: ParentContact,
  defaultCountry: string,
): ParentContactPrefill | null {
  const parentEmail = parentContact.email;
  const parentPhone = parentContact.phone;
  if (parentEmail) {
    const secondary = parentPhone
      ? hydratePhoneFromApi(parentPhone, defaultCountry)
      : { value: '', countryCode: defaultCountry };
    return {
      patch: {
        contact: parentEmail,
        contactType: 'email',
        email: parentEmail,
        phone: parentPhone,
        secondaryContactCountryCode: secondary.countryCode,
      },
      contactType: 'email',
      secondaryContact: secondary.value,
    };
  }
  if (parentPhone) {
    const primary = hydratePhoneFromApi(parentPhone, defaultCountry);
    return {
      patch: {
        contact: primary.value,
        contactType: 'phone',
        phone: primary.value,
        contactCountryCode: primary.countryCode,
      },
      contactType: 'phone',
    };
  }
  return null;
}

/** Level catalog → select options. */
export function buildLevelOptions(
  levels: Array<{ id: string; label: string }>,
): Array<{ value: string; label: string }> {
  return levels.map((level) => ({ value: level.id, label: level.label }));
}

/** Name-keyed catalog → select options (value = name). */
export function buildNamedOptions(
  items: Array<{ name: string }>,
): Array<{ value: string; label: string }> {
  return items.map((item) => ({ value: item.name, label: item.name }));
}

/** True when any selected program is a sport course path. */
export function studentHasSportProgram(
  programs: Array<{ id: string; coursePathType?: string }>,
  selectedProgramIds: string[] | undefined,
): boolean {
  return programs.some((p) => p.coursePathType === 'sport' && selectedProgramIds?.includes(p.id));
}

/** Form payload for the validator: aliases + `_isAdult` / `_contactType`. */
export function studentValidationPayload(
  formData: Record<string, any>,
  isAdult: boolean,
  contactType?: ContactType,
  secondaryContact?: string,
): Record<string, any> {
  return {
    ...formData,
    secondaryContact: isAdult ? (secondaryContact ?? formData.secondaryContact) : '',
    _contactType: contactType ?? formData.contactType ?? 'unknown',
    _isAdult: isAdult,
    department: formData.departmentId || formData.department,
    level: formData.levelId || formData.level,
    schoolEstablishment: formData.schoolEstablishmentIds?.length
      ? formData.schoolEstablishmentIds
      : formData.schoolEstablishment,
  };
}

/** Inherit parent email/phone onto the student form (create + edit). */
export function useStudentParentContactPrefill(params: {
  isAdult: boolean;
  parentUserId?: string;
  parentFullState: unknown;
  defaultCountry: string;
  enabled?: boolean;
  parentContactMap?: Record<string, ParentContact>;
  initialParentUserId?: string;
  initialParentEmail?: string;
  initialParentPhone?: string;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setContactType: (type: ContactType) => void;
  setSecondaryContact: (value: string) => void;
}) {
  const {
    isAdult,
    parentUserId,
    parentFullState,
    defaultCountry,
    enabled = true,
    parentContactMap,
    initialParentUserId,
    initialParentEmail,
    initialParentPhone,
    setFormData,
    setContactType,
    setSecondaryContact,
  } = params;
  const appliedTokenRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!enabled) return;
    const parentKey = linkedParentUserId(parentUserId);
    if (!parentKey) {
      appliedTokenRef.current = null;
      if (isAdult) return;
      setFormData((prev) => ({
        ...prev,
        contact: '',
        contactType: 'unknown',
        email: '',
        phone: '',
      }));
      setContactType('unknown');
      setSecondaryContact('');
      return;
    }

    const fromFull = extractParentContactFromFullState(parentFullState);
    const fromMap = parentContactMap?.[parentKey];
    const fromInitial =
      initialParentUserId === parentKey
        ? { email: initialParentEmail, phone: initialParentPhone }
        : undefined;
    const parentContact = fromFull ?? fromMap ?? fromInitial;
    if (!parentContact) return;

    const token = fromFull ? `${parentKey}:full` : `${parentKey}:fallback`;
    if (appliedTokenRef.current === token || appliedTokenRef.current === `${parentKey}:full`) {
      return;
    }

    const prefill = resolveParentContactPrefill(parentContact, defaultCountry);
    if (!prefill) return;

    appliedTokenRef.current = token;
    setFormData((prev) => {
      if (isAdult && prev.contact) return prev;
      setContactType(prefill.contactType);
      if (prefill.secondaryContact !== undefined) setSecondaryContact(prefill.secondaryContact);
      return { ...prev, ...prefill.patch };
    });
  }, [
    parentUserId,
    parentFullState,
    isAdult,
    defaultCountry,
    parentContactMap,
    initialParentUserId,
    initialParentEmail,
    initialParentPhone,
    enabled,
    setFormData,
    setContactType,
    setSecondaryContact,
  ]);
}

/** True when the student-lives-with-parent radio is Yes. */
export function isLivingWithParents(value: unknown): boolean {
  return value === true || value === 'true';
}

/** Trimmed parent user id. Empty when the student is not linked to a parent. */
export function linkedParentUserId(parentUserId: unknown): string {
  return typeof parentUserId === 'string' ? parentUserId.trim() : '';
}

/**
 * Address, zip, city, and country follow the parent only when the lives-with-parent
 * question is shown and answered Yes. Any other stored value leaves those fields editable.
 */
export function isStudentAddressLockedToParent(
  parentUserId: unknown,
  livingWithParents: unknown,
): boolean {
  if (!linkedParentUserId(parentUserId)) return false;
  return isLivingWithParents(livingWithParents);
}

/** Reset lives-with-parent to false when the parent link is cleared. */
export function applyStudentFormField(
  prev: Record<string, any>,
  key: string,
  value: unknown,
): Record<string, any> {
  const next = { ...prev, [key]: value };
  if (key === 'parentUserId' && !linkedParentUserId(value)) {
    next.livingWithParents = false;
  }
  return next;
}

/** Parent `personal_information` section from a parent full-state payload. */
function extractParentAddressFromFullState(parentFullState: unknown): Record<string, any> | null {
  const personal = (parentFullState as { record?: { sections?: Record<string, unknown> } } | null)
    ?.record?.sections?.personal_information;
  if (!personal || typeof personal !== 'object') return null;
  return personal as Record<string, any>;
}

/** Copy parent address/department onto the student form (living-with-parents). */
function mergeParentAddressIntoForm(
  prev: Record<string, any>,
  personal: Record<string, any>,
): Record<string, any> {
  const next = { ...prev };
  if (personal.address) next.address = personal.address;
  if (personal.zipCode) next.zipCode = personal.zipCode;
  if (personal.city) next.city = personal.city;
  if (personal.country) next.country = personal.country;
  const dept = personal.department as
    | { departmentId?: string; departmentName?: string }
    | string
    | undefined;
  if (!dept) return next;
  if (typeof dept === 'object') {
    next.department = dept;
    next.departmentId = dept.departmentId ?? next.departmentId;
    next.departmentName = dept.departmentName ?? next.departmentName;
  } else {
    next.departmentId = dept;
  }
  return next;
}

/** Fill student address fields from the linked parent's full-state. No-op if parent has no address. */
export function applyParentAddressToStudentForm(
  formData: Record<string, any>,
  parentFullState: unknown,
): Record<string, any> {
  const personal = extractParentAddressFromFullState(parentFullState);
  if (!personal) return formData;
  return mergeParentAddressIntoForm(formData, personal);
}

/**
 * Copies the parent address into the student form when "lives with parents" is Yes.
 * Applies once per parent (Yes → No → Yes does not re-copy). Re-applies if the parent changes.
 */
export function useStudentParentAddressPrefill(params: {
  livingWithParents: unknown;
  parentUserId?: string;
  parentFullState: unknown;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}): void {
  const { livingWithParents, parentUserId, parentFullState, setFormData } = params;
  const appliedForParentRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isStudentAddressLockedToParent(parentUserId, livingWithParents)) return;

    const parentKey = linkedParentUserId(parentUserId);
    if (appliedForParentRef.current === parentKey) return;
    if (parentFullState == null) return;

    appliedForParentRef.current = parentKey;
    setFormData((prev) => applyParentAddressToStudentForm(prev, parentFullState));
  }, [livingWithParents, parentUserId, parentFullState, setFormData]);
}

/** Department code from a `"Name (Code)"` label. */
export function extractDepartmentCodeFromLabel(department: string): string | undefined {
  return parseDepartmentFromLabel(department)?.code;
}

/** Helper text under the contact field (create only; adult vs inherited). */
export function studentContactHelperText(mode: FormMode, isAdult: boolean): string | undefined {
  if (mode === 'edit') return undefined;
  if (isAdult) {
    return "Contact propre : email ou téléphone de l'élève majeur (laisser vide pour utiliser le contact hérité du parent).";
  }
  return "Contact hérité du parent : email ou téléphone du parent lié, utilisé pour joindre l'élève mineur. Le contact propre n'est disponible qu'à partir de 18 ans.";
}

/** Adult contact field state; undefined for minors. */
export function resolveAdultContactState(
  isAdult: boolean,
  existsError: string | undefined,
  validationState: AdultContactFieldState,
): AdultContactFieldState {
  if (!isAdult) return undefined;
  if (existsError) return 'error';
  return validationState;
}

/** Adult contact error; skipped for minors. */
export function resolveAdultContactError(
  isAdult: boolean,
  existsError: string | undefined,
  validationError: string | undefined,
): string | undefined {
  if (!isAdult) return undefined;
  if (existsError) return existsError;
  return validationError;
}

/** Next primary contact fields; reset secondary when the type flips. */
export function applyRemoteStudentPrimaryContactChange(
  previousContactType: ContactKind | undefined,
  value: string,
  type: ContactKind,
  phoneCountryCode?: string,
): { patch: Record<string, unknown>; resetSecondaryContact: boolean } {
  const patch: Record<string, unknown> = {
    contact: value,
    contactType: type,
  };
  if (phoneCountryCode) patch.contactCountryCode = phoneCountryCode;
  if (type === 'email') patch.email = value;
  else if (type === 'phone') patch.phone = value;
  return {
    patch,
    resetSecondaryContact:
      type !== 'unknown' && previousContactType !== 'unknown' && type !== previousContactType,
  };
}

/** Edit of a student whose user status is not yet `active`. */
export function isStudentPreRegistered(mode: FormMode, record?: StudentRecord): boolean {
  return mode === 'edit' && !!record && record.user?.status !== 'active';
}

/** Hide "already participated" fields while a welcome-meeting slot is selected. */
export function shouldShowWelcomeParticipationFields(formData: {
  sessionId?: unknown;
  hasParticipated?: unknown;
}): boolean {
  return !formData.sessionId || formData.hasParticipated === true;
}

/** Welcome-meeting checkbox is locked once already marked participated. */
export function isWelcomeParticipationLocked(
  mode: FormMode,
  fullState: any,
  record?: StudentRecord,
): boolean {
  if (mode !== 'edit') return false;
  const fromFull = (fullState?.record?.sections as any)?.welcome_meeting?.hasParticipated === true;
  const fromRecord = (record?.sections as any)?.welcome_meeting?.hasParticipated === true;
  return fromFull || fromRecord;
}

/** Patch welcome-meeting participation (stamp date + marker on check). */
export function buildWelcomeMeetingParticipationPatch(
  checked: boolean,
  formData: { participatedAt?: unknown; markedBy?: unknown },
  user:
    | {
        id: string;
        firstName?: unknown;
        lastName?: unknown;
        userRoles?: Array<{ role?: { code?: string } }>;
      }
    | null
    | undefined,
  participationLocked: boolean,
): Record<string, unknown> | null {
  if (participationLocked && !checked) return null;
  if (!checked) {
    return {
      hasParticipated: false,
      participatedAt: null,
      markedBy: null,
    };
  }
  const patch: Record<string, unknown> = { hasParticipated: true };
  if (!formData.participatedAt) {
    patch.participatedAt = new Date().toISOString();
  }
  if (user && !formData.markedBy) {
    patch.markedBy = {
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      role: getPrimaryRole(user.userRoles)?.code || 'admin',
    };
  }
  return patch;
}

/** Whether create submit has all required identity / school / matching fields. */
export function isCreateStudentFormComplete(
  formData: Record<string, any>,
  isAdult: boolean,
): boolean {
  return !!(
    formData.firstName &&
    formData.lastName &&
    (isAdult || formData.contact) &&
    formData.dateOfBirth &&
    formData.gender &&
    formData.code?.length === 6 &&
    formData.address &&
    formData.zipCode &&
    formData.city &&
    (formData.departmentId || formData.department) &&
    formData.schoolSituation &&
    (formData.levelId || formData.level) &&
    formData.schoolEstablishmentIds?.length &&
    formData.programs?.length &&
    formData.subject &&
    (Array.isArray(formData.availability)
      ? formData.availability.length > 0
      : !!formData.availability && Object.keys(formData.availability).length > 0)
  );
}

/** Immutable set of a dotted path on a form object. */
export function applyNestedPath(
  prev: Record<string, any>,
  path: string,
  value: unknown,
): Record<string, any> {
  return setNestedPath(prev, path, value);
}

/** Adult invite contact: form primary/secondary, then parent fallback. */
function assignAdultContact(
  dto: Record<string, any>,
  formData: Record<string, any>,
  contactType: ContactType,
  secondaryContact: string,
  resolvedParentContact: ParentContact,
  defaultCountry: string,
): void {
  const effectiveEmail =
    contactType === 'email' ? formData.contact : secondaryContact?.trim() || undefined;
  const rawPhone =
    contactType === 'phone' ? formData.contact : secondaryContact?.trim() || undefined;
  const effectivePhone = rawPhone
    ? phoneToE164(rawPhone, formData.contactCountryCode || defaultCountry)
    : undefined;

  const finalEmail = effectiveEmail || resolvedParentContact?.email;
  const finalPhone = effectivePhone || resolvedParentContact?.phone;

  if (finalEmail) {
    dto.email = finalEmail;
    dto.inviteBy = 'email';
  }
  if (finalPhone) {
    dto.phone = finalPhone;
    if (!finalEmail) dto.inviteBy = 'phone';
  }
  dto.primaryContactKind = primaryContactKindFromType(contactType) ?? dto.inviteBy;
}

/** Copy registration fields onto the create-invite student DTO. */
function assignCreateRegistration(dto: Record<string, any>, formData: Record<string, any>): void {
  assignTruthy(dto, 'gender', formData.gender);
  assignTruthy(dto, 'status', formData.status);
  assignTruthy(dto, 'dateOfBirth', formData.dateOfBirth);
  assignTruthy(dto, 'address', formData.address);
  assignTruthy(dto, 'zipCode', formData.zipCode);
  assignTruthy(dto, 'city', formData.city);
  assignTruthy(dto, 'department', formData.departmentId || formData.department);
  assignTruthy(dto, 'departmentName', formData.departmentName);
  assignTruthy(dto, 'country', formData.country);
  assignTruthy(dto, 'schoolSituation', formData.schoolSituation);
  if (formData.levelId || formData.level) dto.level = formData.level || formData.levelId;
  assignTruthy(dto, 'establishmentDepartment', formData.establishmentDepartmentCode);
  if (formData.schoolEstablishment?.length) {
    dto.schoolEstablishment = formData.schoolEstablishment;
  } else if (formData.schoolEstablishmentIds) {
    dto.schoolEstablishment = formData.schoolEstablishmentIds;
  }
  assignTruthy(dto, 'code', formData.code);
  assignTruthy(dto, 'highSchoolType', formData.highSchoolType);
  assignTruthy(dto, 'schoolCurriculum', formData.schoolCurriculum);
  assignTruthy(dto, 'specificNeeds', formData.specificNeeds);
  assignTruthy(dto, 'legalStatus', formData.legalStatus);
  dto.livingWithParents = formBool(formData.livingWithParents);
}

/** Copy matching fields onto the create-invite student DTO. */
function assignCreateMatching(dto: Record<string, any>, formData: Record<string, any>): void {
  assignTruthy(dto, 'availability', formData.availability);
  assignTruthy(dto, 'programs', formData.programs);
  assignTruthy(dto, 'subject', formData.subject);
  assignTruthy(dto, 'coursePath', formData.coursePath);
  dto.isPartner = formBool(formData.isPartner);
  assignTruthy(dto, 'partnerName', formData.partnerName);
  dto.availableOnWeekends = formBool(formData.availableOnWeekends);
  dto.availableOnVacations = formBool(formData.availableOnVacations);
  dto.availableOnHolidays = formBool(formData.availableOnHolidays);
  assignTruthy(dto, 'comments', formData.comments);
  assignTruthy(dto, 'clubDeSport', formData.clubDeSport);
}

/** Copy welcome-meeting and document fields onto the create DTO. */
function assignCreateWelcomeAndDocs(dto: Record<string, any>, formData: Record<string, any>): void {
  assignDefined(dto, 'sessionId', formData.sessionId);
  dto.hasParticipated = formBool(formData.hasParticipated);
  assignTruthy(dto, 'participatedAt', formData.participatedAt);
  assignDefined(dto, 'markedBy', formData.markedBy);
  assignTruthy(
    dto,
    'schoolReportDocumentLink',
    storedDocumentLink(formData.schoolReport?.documentLink),
  );
  assignTruthy(dto, 'schoolReportDocumentName', formData.schoolReport?.documentName);
}

/** Build the create-invite student DTO from form state. */
export function buildCreateStudentDto(
  formData: Record<string, any>,
  contactType: ContactType,
  secondaryContact: string,
  isAdult: boolean,
  resolvedParentContact: ParentContact,
  defaultCountry: string,
): Record<string, any> {
  const dto: Record<string, any> = {
    firstName: formData.firstName ?? '',
    lastName: formData.lastName ?? '',
  };
  assignTruthy(dto, 'parentUserId', formData.parentUserId);
  if (isAdult) {
    assignAdultContact(
      dto,
      formData,
      contactType,
      secondaryContact,
      resolvedParentContact,
      defaultCountry,
    );
  }
  assignCreateRegistration(dto, formData);
  assignCreateMatching(dto, formData);
  assignCreateWelcomeAndDocs(dto, formData);
  return dto;
}

/** Copy registration fields onto the update student DTO. */
function assignUpdateRegistration(
  dto: Record<string, any>,
  formData: Record<string, any>,
  riBase: Record<string, any> | undefined,
  isPreRegistered: boolean,
): void {
  const riGet = (key: string) => formData[key] ?? riBase?.[key];
  assignTruthy(dto, 'firstName', formData.firstName);
  assignTruthy(dto, 'lastName', formData.lastName);
  assignTruthy(dto, 'gender', riGet('gender'));
  const statusVal = !isPreRegistered ? (formData.status ?? riBase?.status) : formData.status;
  if (statusVal && statusVal !== 'archived') {
    assignTruthy(dto, 'status', statusVal);
  }
  assignTruthy(dto, 'dateOfBirth', riGet('dateOfBirth'));
  assignTruthy(dto, 'address', riGet('address'));
  assignTruthy(dto, 'zipCode', riGet('zipCode'));
  assignTruthy(dto, 'city', riGet('city'));

  const deptFallback = riBase?.department;
  const deptFinal =
    formData.departmentId ||
    formData.department ||
    (typeof deptFallback === 'string' ? deptFallback : (deptFallback as any)?.departmentId);
  assignTruthy(dto, 'department', deptFinal);
  const deptNameVal =
    formData.departmentName ||
    (typeof deptFallback === 'object' ? (deptFallback as any)?.departmentName : undefined);
  assignTruthy(dto, 'departmentName', deptNameVal);

  assignTruthy(dto, 'country', riGet('country'));
  assignTruthy(dto, 'schoolSituation', riGet('schoolSituation'));
  assignTruthy(dto, 'level', formData.level || formData.levelId || riBase?.level);
  assignTruthy(
    dto,
    'schoolEstablishment',
    formData.schoolEstablishment || formData.schoolEstablishmentIds || riBase?.schoolEstablishment,
  );
  const estDeptVal =
    formData.establishmentDepartmentCode ||
    (typeof riBase?.establishmentDepartment === 'string'
      ? riBase.establishmentDepartment
      : (riBase?.establishmentDepartment as any)?.departmentId);
  assignTruthy(dto, 'establishmentDepartment', estDeptVal);
  assignTruthy(dto, 'code', riGet('code'));
  assignTruthy(dto, 'highSchoolType', riGet('highSchoolType'));
  assignTruthy(dto, 'schoolCurriculum', riGet('schoolCurriculum'));
  assignTruthy(dto, 'specificNeeds', riGet('specificNeeds'));
  assignTruthy(dto, 'legalStatus', riGet('legalStatus'));
  dto.livingWithParents = formBool(formData.livingWithParents);
}

/** Copy matching fields onto the update student DTO. */
function assignUpdateMatching(
  dto: Record<string, any>,
  formData: Record<string, any>,
  mdBase: Record<string, any> | undefined,
): void {
  const mdGet = (key: string) => formData[key] ?? mdBase?.[key];
  assignTruthy(dto, 'availability', mdGet('availability'));
  let progFinal = formData.programs;
  if (!progFinal && mdBase?.programs) {
    progFinal = Array.isArray(mdBase.programs)
      ? mdBase.programs.map((p: any) => p.programId ?? p)
      : [];
  }
  assignTruthy(dto, 'programs', progFinal);
  assignTruthy(dto, 'subject', mdGet('subject'));
  assignTruthy(dto, 'coursePath', mdGet('coursePath'));
  dto.isPartner = formBool(formData.isPartner ?? mdBase?.isPartner);
  assignTruthy(dto, 'partnerName', mdGet('partnerName'));
  dto.availableOnWeekends = formBool(mdGet('availableOnWeekends'));
  dto.availableOnVacations = formBool(mdGet('availableOnVacations'));
  dto.availableOnHolidays = formBool(mdGet('availableOnHolidays'));
  assignTruthy(dto, 'comments', mdGet('comments'));
  assignTruthy(dto, 'clubDeSport', mdGet('clubDeSport'));
}

/** Copy welcome-meeting and document fields onto the update DTO. */
function assignUpdateWelcomeAndDocs(
  dto: Record<string, any>,
  formData: Record<string, any>,
  wmBase: Record<string, any> | undefined,
  docBase: Record<string, any> | undefined,
): void {
  const wmGet = (key: string) => formData[key] ?? wmBase?.[key];
  assignDefined(
    dto,
    'sessionId',
    formData.sessionId !== undefined ? formData.sessionId : wmBase?.sessionId,
  );
  dto.hasParticipated = formBool(wmGet('hasParticipated'));
  assignTruthy(dto, 'participatedAt', wmGet('participatedAt'));
  if ('markedBy' in formData || wmBase?.markedBy !== undefined) {
    const mbVal = formData.markedBy !== undefined ? formData.markedBy : wmBase?.markedBy;
    assignDefined(dto, 'markedBy', mbVal);
  }
  assignTruthy(
    dto,
    'schoolReportDocumentLink',
    storedDocumentLink(formData.schoolReport?.documentLink) || docBase?.schoolReport?.documentLink,
  );
  assignTruthy(
    dto,
    'schoolReportDocumentName',
    formData.schoolReport?.documentName || docBase?.schoolReport?.documentName,
  );
}

/** Build the update student DTO, falling back to GET section values. */
export function buildUpdateStudentDto(
  formData: Record<string, any>,
  fullState: any,
  isPreRegistered: boolean,
  isAdult: boolean,
  defaultCountry: string,
): Record<string, any> {
  const dto: Record<string, any> = {};
  const sections = (fullState?.record?.sections as any) ?? {};
  assignUpdateRegistration(dto, formData, sections.registration_information, isPreRegistered);
  if (isAdult && (formData.contactType === 'email' || formData.contactType === 'phone')) {
    assignPrimaryContactFields({
      dto,
      primaryValue: String(formData.contact ?? ''),
      primaryKind: formData.contactType,
      primaryCountry: formData.contactCountryCode,
      secondaryValue: formData.secondaryContact,
      secondaryCountry: formData.secondaryContactCountryCode,
      defaultCountry,
    });
  }
  assignUpdateMatching(dto, formData, sections.matching_data);
  assignUpdateWelcomeAndDocs(dto, formData, sections.welcome_meeting, sections.documents);
  return dto;
}

/** Upload a school-report file; returns the download URL. */
export function defaultUpload(
  file: File,
  companyId: string,
  studentUserId: string,
): Promise<string> {
  return uploadStudentSchoolReport(file, companyId, studentUserId);
}

/** `GET /foot-clubs` for select lists. */
export function useSportClubOptions() {
  return useQuery({
    queryKey: ['sport-clubs', 'options'],
    queryFn: getSportClubs,
  });
}

/** Catalog label for a reactivation status code. */
export function getReactivationStatusLabel(status: string): string {
  return STUDENT_ACCOUNT_STATUS_CONFIG[status]?.label ?? status;
}

/** Catalog label for a reactivation situation, or `'—'`. */
export function getReactivationSituationLabel(situation: string | null): string {
  if (!situation) return '—';
  return STUDENT_SITUATION_CONFIG[situation as StudentSituation]?.label ?? situation;
}

/** Current vs next status/situation labels for the reactivation confirm. */
export function formatReactivationPreview(preview: ReactivationPreview): {
  currentStatus: string;
  nextStatus: string;
  currentSituation: string;
  nextSituation: string;
} {
  return formatSharedReactivationPreview(
    preview,
    getReactivationStatusLabel,
    getReactivationSituationLabel,
  );
}

/** Catalogued reason why a student cannot be reactivated. */
export function getStudentReactivationBlockMessage(reason: string | undefined): string {
  if (reason === 'no_parent') return getErrorMessage('reactivationNoParent');
  if (reason === 'parent_not_active') return getErrorMessage('reactivationParentNotActive');
  if (reason === 'parent_reactivatable') return getErrorMessage('reactivationParentFirstHint');
  return getErrorMessage('reactivationNotEligible');
}

/** Shallow-copy the form, then write the uploaded school-report URL when present. */
export function mergePendingUploads<T extends { schoolReport?: { documentLink?: string } }>(
  formData: T,
  uploadedUrls: Record<string, string>,
): T {
  if (!uploadedUrls.schoolReportFile) return { ...formData };
  return {
    ...formData,
    schoolReport: {
      ...formData.schoolReport,
      documentLink: uploadedUrls.schoolReportFile,
    },
  };
}
